/** Plain-English audit sentences. No card numbers. */

export type AuditItemLine = { name?: string; quantity?: number };

const CHANNEL_LABELS: Array<[string, string]> = [
  ['availableOnline', 'online'],
  ['availableQr', 'QR'],
  ['availableDineIn', 'dine-in'],
  ['availableTakeaway', 'takeaway'],
  ['availableDelivery', 'delivery'],
  ['availableMealPlan', 'meal plan'],
];

export function formatAuditMoney(amount: number): string {
  if (!Number.isFinite(amount)) return '';
  return `$${amount.toFixed(2)}`;
}

export function formatAuditPickup(ms: number | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '';
  return new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(ms));
}

export function formatAuditItemList(items: AuditItemLine[]): string {
  return items
    .map((item) => {
      const name = String(item.name || 'Item').trim();
      const qty = Number(item.quantity ?? 1);
      return qty > 1 ? `${name} x${qty}` : name;
    })
    .filter(Boolean)
    .join(', ');
}

export function onlineOrderPlacedSummary(
  items: AuditItemLine[],
  total: number,
  pickupTime?: number,
): string {
  const pickup = formatAuditPickup(pickupTime);
  const lines = formatAuditItemList(items);
  return [
    'Online order placed',
    formatAuditMoney(total),
    pickup ? `pickup ${pickup}` : '',
    lines,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function onlinePaymentStartedSummary(amount: number): string {
  return `Windcave payment started · ${formatAuditMoney(amount)}`;
}

export function onlinePaymentSettledSummary(input: {
  authorised: boolean;
  amount?: number;
  transactionId?: string;
  source: 'fprn' | 'browser' | 'sweep';
}): string {
  const outcome = input.authorised ? 'Payment approved' : 'Payment declined';
  const via = input.source === 'sweep' ? 'sweep' : input.source === 'fprn' ? 'notification' : 'customer return';
  return [
    outcome,
    input.amount != null ? formatAuditMoney(input.amount) : '',
    'Windcave',
    input.transactionId ? `txn ${input.transactionId}` : '',
    `via ${via}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function onlineCheckoutAbandonedSummary(): string {
  return 'Customer cancelled the unpaid online checkout';
}

export function onlineFprnRejectedSummary(reason: string): string {
  return `Windcave notification rejected · ${reason}`;
}

export function onlinePaymentNotAppliedSummary(reason: string): string {
  return `Windcave payment was not applied to the order · ${reason}`;
}

type LineLike = {
  lineId?: string;
  id?: string | number;
  name?: string;
  quantity?: number;
  notes?: string;
  removed?: boolean;
  selectedMeatOption?: string;
};

function sameDish(a: LineLike, b: LineLike): boolean {
  return String(a.id ?? '') === String(b.id ?? '')
    && (a.name || '') === (b.name || '')
    && (a.selectedMeatOption || '') === (b.selectedMeatOption || '');
}

export function orderEditSummaries(
  beforeItems: LineLike[],
  afterItems: LineLike[],
): { removed: string[]; changed: string[] } {
  const removed: string[] = [];
  const changed: string[] = [];
  const usedAfter = new Set<number>();

  const pairIndex = (item: LineLike, index: number): number => {
    if (item.lineId) {
      const byLine = afterItems.findIndex((candidate, candidateIndex) => !usedAfter.has(candidateIndex) && candidate.lineId === item.lineId);
      if (byLine >= 0) return byLine;
    }
    const byIndex = afterItems.findIndex((candidate, candidateIndex) =>
      candidateIndex === index && !usedAfter.has(candidateIndex) && sameDish(item, candidate),
    );
    if (byIndex >= 0) return byIndex;
    return afterItems.findIndex((candidate, candidateIndex) => !usedAfter.has(candidateIndex) && sameDish(item, candidate));
  };

  beforeItems.forEach((item, index) => {
    const name = String(item.name || 'Item');
    const afterIndex = pairIndex(item, index);
    if (afterIndex < 0) {
      if (item.removed !== true) removed.push(`${name} removed`);
      return;
    }
    usedAfter.add(afterIndex);
    const next = afterItems[afterIndex];
    if (item.removed !== true && next.removed === true) {
      removed.push(`${name} removed`);
      return;
    }
    if (item.removed === true) return;
    const notesChanged = (item.notes ?? '') !== (next.notes ?? '');
    const qtyChanged = Number(item.quantity ?? 0) !== Number(next.quantity ?? 0);
    if (qtyChanged || notesChanged) {
      const bits = [name];
      if (qtyChanged) bits.push(`qty ${item.quantity ?? 0} → ${next.quantity ?? 0}`);
      if (notesChanged) bits.push('notes changed');
      changed.push(bits.join(' · '));
    }
  });

  afterItems.forEach((item, index) => {
    if (usedAfter.has(index) || item.removed === true) return;
    const qty = Number(item.quantity ?? 1);
    changed.push(qty > 1 ? `${item.name || 'Item'} added x${qty}` : `${item.name || 'Item'} added`);
  });

  return { removed, changed };
}

type FlagRecord = Record<string, unknown>;

/** Omitted and true both mean the channel is on. Only a real on/off change is logged. */
function channelIsOn(value: unknown): boolean {
  return value !== false;
}

function flagSentence(name: string, key: string, label: string, before: FlagRecord, after: FlagRecord): string | null {
  if (after[key] === undefined) return null;
  const beforeOn = channelIsOn(before[key]);
  const afterOn = channelIsOn(after[key]);
  if (beforeOn === afterOn) return null;
  return afterOn ? `${name} turned on for ${label}` : `${name} turned off for ${label}`;
}

export function menuItemChangeSummaries(name: string, before: FlagRecord, updates: FlagRecord): string[] {
  const lines: string[] = [];
  if (updates.isAvailable !== undefined && channelIsOn(updates.isAvailable) !== channelIsOn(before.isAvailable)) {
    lines.push(
      updates.isAvailable === false ? `${name} turned off (was available)` : `${name} turned on`,
    );
  }
  if (typeof updates.name === 'string' && updates.name !== before.name) {
    lines.push(`Menu item renamed · ${String(before.name || name)} → ${updates.name}`);
  }
  if (updates.price !== undefined && Number(updates.price) !== Number(before.price)) {
    lines.push(`${name} price ${formatAuditMoney(Number(before.price))} → ${formatAuditMoney(Number(updates.price))}`);
  }
  for (const [key, label] of CHANNEL_LABELS) {
    const sentence = flagSentence(name, key, label, before, updates);
    if (sentence) lines.push(sentence);
  }
  return lines;
}

export function menuCategoryChangeSummaries(name: string, before: FlagRecord, updates: FlagRecord): string[] {
  const lines: string[] = [];
  if (typeof updates.name === 'string' && updates.name !== before.name) {
    lines.push(`Category renamed · ${String(before.name || name)} → ${updates.name}`);
  }
  for (const [key, label] of CHANNEL_LABELS) {
    const sentence = flagSentence(name, key, label, before, updates);
    if (sentence) lines.push(sentence);
  }
  return lines;
}

export function onlineSettingsSummary(input: {
  beforeEnabled: boolean;
  afterEnabled: boolean;
  hoursChanged: boolean;
}): string | null {
  const parts: string[] = [];
  if (input.beforeEnabled !== input.afterEnabled) {
    parts.push(input.afterEnabled ? 'Online ordering turned on' : 'Online ordering turned off');
  }
  if (input.hoursChanged) parts.push('Business hours saved');
  return parts.length ? parts.join(' · ') : null;
}
